import { useEffect, useState } from "react";
import { CheckCircle2, Copy, ExternalLink, Unplug } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, Panel, Pill, PageLoader, copyText } from "@/components/kit";
import { api, errMsg, fmtDay } from "@/lib/api";
import { SectionHead } from "./common";

const Step = ({ n, title, children }) => (
  <div className="flex gap-4">
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#CCFF00] font-mono text-xs font-bold text-[#0B0F17]">{n}</span>
    <div className="min-w-0 flex-1 text-sm text-slate-300"><p className="font-semibold text-white">{title}</p>{children}</div>
  </div>
);

const CopyRow = ({ value, testid }) => (
  <div className="mt-2 flex items-center gap-2 rounded-md border border-white/10 bg-black/30 px-3 py-2">
    <code className="min-w-0 flex-1 break-all font-mono text-xs text-[#CCFF00]" data-testid={testid}>{value}</code>
    <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyText(value, "Copied")} data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
  </div>
);

const Guide = ({ s }) => (
  <Panel className="space-y-5" data-testid="stripe-setup-guide">
    <p className="font-display text-xl font-bold uppercase text-white">How to set it up</p>
    <Step n="1" title="Get your secret key">
      In your <a className="text-[#CCFF00] underline" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer">Stripe dashboard → Developers → API keys</a>, reveal and copy the <b>Secret key</b> (starts with <code>sk_live_</code>, or <code>sk_test_</code> while you test).
    </Step>
    <Step n="2" title="Add a webhook endpoint (recommended)">
      Go to <a className="text-[#CCFF00] underline" href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer">Developers → Webhooks</a> → <b>Add endpoint</b> and paste this URL:
      <CopyRow value={`${process.env.REACT_APP_BACKEND_URL}/api/stripe/webhook/team/${s.webhook_url.split("/").pop()}`} testid="stripe-webhook-url" />
      <p className="mt-3">Select these events:</p>
      <div className="mt-2 flex flex-wrap gap-2">{s.webhook_events.map((e) => <code key={e} className="rounded bg-white/5 px-2 py-1 font-mono text-xs text-slate-200" data-testid="stripe-webhook-event">{e}</code>)}</div>
    </Step>
    <Step n="3" title="Copy the signing secret">After saving the endpoint, click <b>Reveal</b> under “Signing secret” (starts with <code>whsec_</code>) and paste it below.</Step>
    <p className="text-xs text-slate-500">Without the webhook, payments still confirm when the buyer returns to your hub — the webhook makes sure nothing is missed if they close the page early, and keeps refunds in sync.</p>
  </Panel>
);

export default function StripeAccount({ teamId }) {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ secret_key: "", webhook_secret: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/teams/${teamId}/stripe`).then((r) => setS(r.data));
  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/teams/${teamId}/stripe`, f);
      setS(data);
      setF({ secret_key: "", webhook_secret: "" });
      toast.success("Stripe account saved");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect your Stripe account? Your shop closes and fee payments go back to the platform account.")) return;
    await api.delete(`/teams/${teamId}/stripe`);
    toast.success("Disconnected");
    load();
  };
  if (!s) return <PageLoader />;

  return (
    <div className="space-y-6">
      <SectionHead title="Stripe account" text="Connect your club's own Stripe account so shop sales and player fees are paid straight to you." />
      <Panel data-testid="stripe-status-panel">
        {s.connected ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              <div>
                <p className="font-semibold text-white" data-testid="stripe-account-name">Connected{ s.account_name ? ` · ${s.account_name}` : ""}</p>
                <p className="font-mono text-xs text-slate-500">{s.key_hint} · since {fmtDay(s.connected_at)}</p>
              </div>
              <Pill tone={s.livemode ? "green" : "amber"} data-testid="stripe-mode-pill">{s.livemode ? "Live" : "Test mode"}</Pill>
              <Pill tone={s.has_webhook_secret ? "green" : "amber"} data-testid="stripe-webhook-pill">{s.has_webhook_secret ? "Webhook set" : "No webhook"}</Pill>
            </div>
            <Button variant="ghost" className="text-rose-400" onClick={disconnect} data-testid="stripe-disconnect-btn"><Unplug className="mr-1 h-4 w-4" /> Disconnect</Button>
          </div>
        ) : <p className="text-sm text-slate-400" data-testid="stripe-not-connected">Not connected yet — player fees are collected through the platform account and the club shop stays closed.</p>}
      </Panel>
      <div className="grid gap-6 xl:grid-cols-[1.2fr_1fr]">
        <Guide s={s} />
        <Panel>
          <form onSubmit={save} className="space-y-4" data-testid="stripe-form">
            <Field label="Secret key" hint={s.connected ? "Leave blank to keep the current key" : "Stored encrypted — never shown again"}>
              <Input type="password" autoComplete="off" value={f.secret_key} onChange={(e) => setF({ ...f, secret_key: e.target.value })} placeholder="sk_live_…" data-testid="stripe-secret-input" />
            </Field>
            <Field label="Webhook signing secret" hint={s.has_webhook_secret ? "Leave blank to keep the current secret" : "From step 3"}>
              <Input type="password" autoComplete="off" value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} placeholder="whsec_…" data-testid="stripe-webhook-secret-input" />
            </Field>
            <Button type="submit" disabled={busy} className="w-full rounded-full font-bold uppercase" data-testid="stripe-save-btn">{busy ? "Checking with Stripe…" : s.connected ? "Update" : "Connect Stripe"}</Button>
            <a href="https://dashboard.stripe.com/register" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1 text-xs text-slate-500 hover:text-white" data-testid="stripe-register-link">No Stripe account yet? Create one <ExternalLink className="h-3 w-3" /></a>
          </form>
        </Panel>
      </div>
    </div>
  );
}
